/**
 * RuntimeSelectionManager
 *
 * Manages runtime selection callbacks for behaviors that need to respond
 * to selection (e.g. Moveable, Properties :selected state).
 *
 * Multiple behaviors can register callbacks on the same SceneObject.
 * A single selection-changed listener dispatches to all registered callbacks.
 */

import type { SceneObject } from "../core/SceneObject";
import type { TraceScene } from "../core/TraceScene";
import { Logger } from "../utils/Logger";

export interface SelectionCallbacks {
    /** Called when this object becomes selected. */
    onSelected?: () => void;
    /** Called when this object is deselected (another object selected, or selection cleared). */
    onDeselected?: () => void;
}

export class RuntimeSelectionManager {
    private readonly _traceScene: TraceScene;
    /** Multiple callback sets per object — supports Moveable + Properties coexisting */
    private readonly _registered = new Map<SceneObject, SelectionCallbacks[]>();
    private _selectionHandler: ((e: Event) => void) | null = null;
    private _currentlySelected: SceneObject | null = null;

    constructor(traceScene: TraceScene) {
        this._traceScene = traceScene;
    }

    /**
     * Register a SceneObject as selectable at runtime.
     * Multiple behaviors can register on the same object.
     * Returns a removal function.
     */
    public register(sceneObject: SceneObject, callbacks: SelectionCallbacks): () => void {
        const wasEmpty = this._registered.size === 0;

        let list = this._registered.get(sceneObject);
        if (!list) {
            list = [];
            this._registered.set(sceneObject, list);
        }
        list.push(callbacks);

        if (wasEmpty) {
            this._listenForSelection();
        }

        return () => {
            const arr = this._registered.get(sceneObject);
            if (!arr) return;
            const idx = arr.indexOf(callbacks);
            if (idx >= 0) arr.splice(idx, 1);
            if (arr.length === 0) {
                this._registered.delete(sceneObject);
                if (this._currentlySelected === sceneObject) {
                    this._currentlySelected = null;
                }
                if (this._registered.size === 0) {
                    this._disable();
                }
            }
        };
    }

    /**
     * Unregister ALL callbacks for a SceneObject.
     */
    public unregister(sceneObject: SceneObject): void {
        if (!this._registered.delete(sceneObject)) return;

        if (this._currentlySelected === sceneObject) {
            this._currentlySelected = null;
        }

        if (this._registered.size === 0) {
            this._disable();
        }
    }

    /** Returns true if the object has registered for runtime selection. */
    public isRegistered(sceneObject: SceneObject): boolean {
        return this._registered.has(sceneObject);
    }

    /**
     * Select an object via TraceScene.
     */
    public select(sceneObject: SceneObject): void {
        this._traceScene.select(sceneObject);
    }

    public dispose(): void {
        this._disable();
        this._registered.clear();
        this._currentlySelected = null;
    }


    private _disable(): void {
        this._removeSelectionListener();
        Logger.log("[RuntimeSelectionManager] Disabled (no selectables registered)");
    }

    private _listenForSelection(): void {
        if (this._selectionHandler) return;

        const events = this._traceScene.getEventSystem();
        if (!events) return;

        this._selectionHandler = (e: Event) => {
            const detail = (e as CustomEvent<{ object?: SceneObject | null }>).detail;
            const newSelected = detail?.object ?? null;

            const matched = newSelected && this._registered.has(newSelected) ? newSelected : null;
            if (matched === this._currentlySelected) return;

            // Notify previous — all callbacks
            if (this._currentlySelected) {
                const callbacks = this._registered.get(this._currentlySelected);
                if (callbacks) {
                    for (const cb of callbacks) cb.onDeselected?.();
                }
            }

            this._currentlySelected = matched;

            // Notify new — all callbacks
            if (matched) {
                const callbacks = this._registered.get(matched);
                if (callbacks) {
                    for (const cb of callbacks) cb.onSelected?.();
                }
            }
        };
        events.addEventListener('trace-viewer:selection-changed', this._selectionHandler);
    }

    private _removeSelectionListener(): void {
        if (!this._selectionHandler) return;

        const events = this._traceScene.getEventSystem();
        if (events) {
            events.removeEventListener('trace-viewer:selection-changed', this._selectionHandler);
        }
        this._selectionHandler = null;
    }
}
